/**
 * Global-search UI configs: limits, icons, copy, and dynamic-menu exclusions.
 */
import type { MenuSearchResult, SearchResultType } from '@/types/search.types';

/** Icon color token on a command-palette row. */
export type SearchIconColor = 'color-1' | 'color-2' | 'color-3';

/** Key used with Ctrl/⌘ to focus the navbar search field. */
export const SEARCH_SHORTCUT_KEY = 'k';

/** Minimum query length before menus / API search run. */
export const SEARCH_MIN_QUERY_LENGTH = 2;

/** Page size for data API results and menu pagination. */
export const SEARCH_PAGE_SIZE = 5;

/** Debounce before `GET /search/global` after the query changes. */
export const SEARCH_DEBOUNCE_MS = 300;

/** Lucide name per entity kind. */
export const SEARCH_RESULT_ICON_MAP: Record<SearchResultType, string> = {
  tutor: 'GraduationCap',
  student: 'User',
  volunteer: 'Heart',
  admin: 'Shield',
  establishment: 'Building2',
};

/** Header icon color per entity kind. */
export const SEARCH_RESULT_ICON_COLOR_MAP: Record<SearchResultType, SearchIconColor> = {
  tutor: 'color-1',
  student: 'color-2',
  volunteer: 'color-3',
  admin: 'color-1',
  establishment: 'color-2',
};

/** Rotating colors for menu rows in the palette. */
export const SEARCH_MENU_COLOR_CYCLE: SearchIconColor[] = ['color-1', 'color-2', 'color-3'];

/** Sublabel on a menu row (create vs navigation). */
export const SEARCH_MENU_SUBLABEL = {
  create: 'Action',
  view: 'Navigation',
} as const;

/** Sublabel on an establishment data hit. */
export const SEARCH_ESTABLISHMENT_SUBLABEL = 'Établissement';

/** Section titles in the dropdown. */
export const SEARCH_SECTION_LABELS = {
  data: 'Données',
  menus: 'Menus',
} as const;

/** Empty / loading / hint copy in the dropdown. */
export const SEARCH_COPY = {
  minLength: 'Tapez au moins 2 caractères pour rechercher',
  loading: 'Recherche en cours...',
  emptyTitle: 'Aucun résultat trouvé',
  emptyDescription: "Essayez avec d'autres mots-clés",
} as const;

/** Sidebar resources that get tab / create menus instead of a generic sidebar row. */
export const SEARCH_GROUP_RESOURCES = new Set(['admins', 'tutors', 'volunteers']);

/** Static menu ids that only apply in remote mode. */
export const REMOTE_ONLY_MENU_IDS = new Set(['menu-reports-remote-view']);

/** Static menu IDs generated dynamically — dropped from the search-menus catalog to avoid duplicates. */
export const DYNAMIC_MENUS_TO_EXCLUDE = new Set([
  'menu-admins-view-admin',
  'menu-admins-view-superadmin',
  'menu-admins-view-coordinator',
  'menu-admins-view-manager',
  'menu-admins-create-admin',
  'menu-admins-create-superadmin',
  'menu-admins-create-coordinator',
  'menu-admins-create-manager',
  'menu-admins-view-coordinator-remote',
  'menu-admins-view-manager-remote',
  'menu-admins-view-recruiter-remote',
  'menu-admins-create-coordinator-remote',
  'menu-admins-create-manager-remote',
  'menu-admins-create-recruiter-remote',
  'menu-tutors-view',
  'menu-tutors-create',
  'menu-volunteers-view',
  'menu-volunteers-create',
]);

/** Generic view row when the admins interface has at least two tabs. */
export const ADMINS_GENERIC_VIEW_MENU: MenuSearchResult = {
  id: 'menu-admins-view',
  label: 'Consulter les administrateurs',
  type: 'view',
  route: '/admins',
  icon: 'Shield',
  keywords: ['administrateurs', 'admins', 'consulter', 'voir', 'collaborateurs'],
  resource: 'admins',
  excludeIfSingleAdminTab: true,
};

/** Generic create row when the admins interface has at least two create options. */
export const ADMINS_GENERIC_CREATE_MENU: MenuSearchResult = {
  id: 'menu-admins-create',
  label: 'Créer un administrateur',
  type: 'create',
  route: '/admins?create=true',
  icon: 'UserPlus',
  keywords: ['administrateur', 'admin', 'créer', 'ajouter', 'nouveau', 'collaborateur'],
  resource: 'admins',
  requiresWrite: true,
  excludeIfSingleAdminTab: true,
};

/** Generic view row when the tutors interface has at least two tabs. */
export const TUTORS_GENERIC_VIEW_MENU: MenuSearchResult = {
  id: 'menu-tutors-view',
  label: 'Consulter les tuteurs',
  type: 'view',
  route: '/tutors',
  icon: 'GraduationCap',
  keywords: ['tuteurs', 'tutors', 'consulter', 'voir'],
  resource: 'tutors',
  excludeIfSingleAdminTab: true,
};

/** Generic create row when the tutors interface has at least two create options. */
export const TUTORS_GENERIC_CREATE_MENU: MenuSearchResult = {
  id: 'menu-tutors-create',
  label: 'Créer un tuteur',
  type: 'create',
  route: '/tutors?create=true',
  icon: 'UserPlus',
  keywords: ['tuteur', 'tutor', 'créer', 'ajouter', 'nouveau'],
  resource: 'tutors',
  requiresWrite: true,
  excludeIfSingleAdminTab: true,
};

/** Generic view row when the volunteers interface has at least two tabs. */
export const VOLUNTEERS_GENERIC_VIEW_MENU: MenuSearchResult = {
  id: 'menu-volunteers-view',
  label: 'Consulter les volontaires',
  type: 'view',
  route: '/volunteers',
  icon: 'Heart',
  keywords: ['volontaires', 'volunteers', 'vsc', 'consulter', 'voir'],
  resource: 'volunteers',
  excludeIfSingleAdminTab: true,
};

/** Generic create row when the volunteers interface has at least two create options. */
export const VOLUNTEERS_GENERIC_CREATE_MENU: MenuSearchResult = {
  id: 'menu-volunteers-create',
  label: 'Créer un volontaire',
  type: 'create',
  route: '/volunteers?create=true',
  icon: 'UserPlus',
  keywords: ['volontaire', 'volunteer', 'vsc', 'créer', 'ajouter', 'nouveau'],
  resource: 'volunteers',
  requiresWrite: true,
  excludeIfSingleAdminTab: true,
};

/** Static roles-management menus injected into the command palette. */
export const ROLES_MANAGEMENT_MENUS: MenuSearchResult[] = [
  {
    id: 'menu-roles-view',
    label: 'Gérer les rôles',
    type: 'view',
    route: '/roles',
    icon: 'Shield',
    keywords: ['rôles', 'roles', 'gestion', 'permissions', 'accès'],
    resource: 'roles',
  },
  {
    id: 'menu-roles-create',
    label: 'Créer un rôle',
    type: 'create',
    route: '/roles?create=true',
    icon: 'Shield',
    keywords: ['rôle', 'role', 'créer', 'ajouter', 'nouveau'],
    resource: 'roles',
    requiresWrite: true,
  },
];
